/**
 * AlertsBanner - Display active alerts in header
 */

import { useLayoutEffect, useMemo, useRef } from 'react';
import { AlertCircle, AlertTriangle, X, Volume2, VolumeX } from 'lucide-react';
import { useDevicePrefs, setDevicePrefs } from '../../utils/devicePrefs';

const ALERTS_BANNER_H_VAR = '--alerts-banner-h';

export interface Alert {
  id: string;
  severity: 'warning' | 'critical';
  title: string;
  message: string;
  timestamp: Date;
  roomId?: string;
  roomName?: string;
  acknowledged: boolean;
}

export interface AlertsBannerProps {
  alerts: Alert[];
  onAcknowledge: (alertId: string) => void;
  onAcknowledgeMany: (alertIds: string[]) => void;
  onAcknowledgeAll: () => void;
  onViewAll: () => void;
  maxVisible?: number;
}

interface GroupedAlert {
  key: string;
  severity: 'warning' | 'critical';
  title: string;
  roomName?: string;
  ids: string[];
  count: number;
}

function groupAlerts(alerts: Alert[]): GroupedAlert[] {
  const map = new Map<string, GroupedAlert>();

  for (const alert of alerts) {
    const key = `${alert.roomId || alert.roomName || 'system'}::${alert.title}`;
    const existing = map.get(key);
    if (existing) {
      existing.ids.push(alert.id);
      existing.count += 1;
      if (alert.severity === 'critical') existing.severity = 'critical';
    } else {
      map.set(key, {
        key,
        severity: alert.severity,
        title: alert.title,
        roomName: alert.roomName,
        ids: [alert.id],
        count: 1,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1;
    return b.count - a.count;
  });
}

export function AlertsBanner({
  alerts,
  onAcknowledge,
  onAcknowledgeMany,
  onAcknowledgeAll,
  onViewAll,
  maxVisible = 3,
}: AlertsBannerProps) {
  const activeAlerts = alerts.filter(a => !a.acknowledged);
  const criticalCount = activeAlerts.filter(a => a.severity === 'critical').length;
  const { soundEnabled } = useDevicePrefs();
  const bannerRef = useRef<HTMLDivElement>(null);

  const groupedAlerts = useMemo(() => groupAlerts(activeAlerts), [activeAlerts]);
  const layoutKey = `${activeAlerts.length}:${criticalCount}:${groupedAlerts.length}:${maxVisible}`;

  // Publish measured height so in-page sticky bars (irrigation header / program chips)
  // can stack below this banner instead of tunneling content through a gap.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (activeAlerts.length === 0) {
      root.style.setProperty(ALERTS_BANNER_H_VAR, '0px');
      return () => root.style.setProperty(ALERTS_BANNER_H_VAR, '0px');
    }
    const el = bannerRef.current;
    if (!el) {
      root.style.setProperty(ALERTS_BANNER_H_VAR, '0px');
      return;
    }
    const publish = () => {
      root.style.setProperty(ALERTS_BANNER_H_VAR, `${Math.ceil(el.getBoundingClientRect().height)}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty(ALERTS_BANNER_H_VAR, '0px');
    };
  }, [activeAlerts.length, layoutKey]);

  if (activeAlerts.length === 0) {
    return null;
  }

  const visibleGroups = groupedAlerts.slice(0, maxVisible);
  const hiddenGroupCount = groupedAlerts.length - maxVisible;

  return (
    <div
      ref={bannerRef}
      className={`sticky top-0 shrink-0 px-3 lg:px-4 py-1.5 lg:py-2.5 flex items-center gap-2 lg:gap-4 w-full z-20 ${
        criticalCount > 0 ? 'bg-danger text-background' : 'bg-warning text-background'
      }`}
    >
      {/* Alert count badge — always visible; sole content on compact besides View All */}
      <div className="bg-background/15 px-2.5 lg:px-3 py-1.5 lg:py-2 rounded-xl text-xs lg:text-sm font-semibold flex items-center gap-1.5 shrink-0">
        <AlertTriangle size={16} />
        {activeAlerts.length}
        {criticalCount > 0 && (
          <span className="text-background">({criticalCount} critical)</span>
        )}
      </div>

      {/* Grouped alert chips — desktop only; compact uses count + View All */}
      <div className="hidden lg:flex gap-3 flex-1 min-w-0 items-center overflow-x-auto no-scrollbar">
        {visibleGroups.map(group => (
          <div
            key={group.key}
            className="bg-background/10 px-4 py-2 rounded-xl flex items-center gap-2 whitespace-nowrap shrink-0"
          >
            <span className="text-background text-sm font-medium flex items-center gap-1.5">
              {group.severity === 'critical' ? (
                <AlertCircle size={14} className="text-background shrink-0" />
              ) : (
                <AlertTriangle size={14} className="text-background shrink-0" />
              )}
              {group.roomName && <strong>{group.roomName}:</strong>}
              {group.title}
              {group.count > 1 && (
                <span className="text-background text-xs font-semibold bg-background/10 px-1.5 py-0.5 rounded-md">
                  ×{group.count}
                </span>
              )}
            </span>
            <button
              type="button"
              className="bg-transparent border-none text-background/80 hover:text-background cursor-pointer p-1 flex items-center transition-colors rounded-lg hover:bg-background/15 tap:min-h-12 tap:min-w-12"
              onClick={() =>
                group.count > 1
                  ? onAcknowledgeMany(group.ids)
                  : onAcknowledge(group.ids[0])
              }
              title={group.count > 1 ? `Dismiss all ${group.count}` : 'Dismiss'}
              aria-label="Acknowledge"
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {hiddenGroupCount > 0 && (
          <button
            type="button"
            className="bg-background/10 px-4 py-2 rounded-xl flex items-center gap-2 whitespace-nowrap border-none cursor-pointer hover:bg-background/20 transition-colors shrink-0 tap:min-h-12"
            onClick={onViewAll}
          >
            <span className="text-background text-sm font-medium">+{hiddenGroupCount} more</span>
          </button>
        )}
      </div>

      {/* Spacer on compact so View All sits on the right */}
      <div className="flex-1 lg:hidden" />

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          className="hidden lg:flex bg-background/10 hover:bg-background/20 border-none rounded-xl p-2 text-background cursor-pointer items-center transition-colors tap:min-h-12 tap:min-w-12"
          onClick={() => setDevicePrefs({ soundEnabled: !soundEnabled })}
          title={soundEnabled ? 'Mute alert sound on this device' : 'Unmute alert sound on this device'}
          aria-label={soundEnabled ? 'Mute alert sound' : 'Unmute alert sound'}
        >
          {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} className="opacity-70" />}
        </button>
        <button
          type="button"
          className="hidden lg:block bg-background/10 hover:bg-background/20 border-none rounded-xl px-3 py-2 text-background cursor-pointer font-medium text-sm transition-colors whitespace-nowrap tap:min-h-12"
          onClick={onAcknowledgeAll}
        >
          Dismiss all
        </button>
        <button
          type="button"
          className="bg-background/15 hover:bg-background/20 border-none rounded-xl px-3 lg:px-4 py-1.5 lg:py-2 text-background cursor-pointer font-medium text-xs lg:text-sm transition-colors whitespace-nowrap tap:min-h-12"
          onClick={onViewAll}
        >
          View All →
        </button>
      </div>
    </div>
  );
}

export default AlertsBanner;
